import { useCallback, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { Ship } from "../../App/Ship"
import { GetDay, GetLog, GetShip } from "../../App/Context"
import { Time } from "../../App/Time"
import { useEditState } from "../../App/useEditState"
import { Block, BlockField, BlockFieldInputs, BlockFieldInputsDivider, BlockRow } from "../../Components/Block"
import { EditGroup, saveErrorMessage } from "../../Components/EditGroup"
import { Icon } from "../../Components/Icon"
import { Select } from "../../Components/Select"
import { PredefinedSelect } from "../../Components/PredefinedSelect"
import { ApiClient, IEditPermissivenessLevel, ILog, ILogEditableField, LogApi, ILogResponse, ILogWeather } from "../../Types"
import { logFieldEditable } from "./editability"

export const LogEnvironmentViewData = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  saveLog(api: ApiClient, shipId: number, logId: number, values: any): Promise<ILogResponse> {
    return new LogApi(api).logs.update({ params: { shipId, logId }, body: values }).call();
  },
  // Predefined codes for the `currentDirection`/`seaState` `PredefinedSelect`s below --
  // hoisted here (rather than local consts in the component) so `LogViewData.
  // fieldFormat` (issue #529, in `LogView.tsx`) can resolve the exact same set of
  // codes to their translated label for `EditHistory`, without duplicating (and
  // risking drifting from) this list.
  predefinedDirections: ["0", "1", "2", "3"],
  predefinedSeaStates: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
};

interface LogEnvironmentViewProps {
  editable: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  onClick?: any;
  // Issue #1136: reports the save response's authoritative log back to `LogView`, so
  // its edit history resolves a just-saved field's "new value" from it rather than
  // from the (still pre-edit) `log` the day's `logs` list holds until the next live
  // broadcast / full day load. Optional: harnesses rendering this view standalone
  // have no edit history to keep in sync.
  recordSavedLog?: (log: ILog) => void;
  // Seeds the save-error state (issue #529) — for catalog/demo fixtures only;
  // production always starts with no error, only ever setting one via a real
  // rejected `onSave`.
  initSaveError?: unknown;
}

export function LogEnvironmentView({ editable, initSaveError, onClick, recordSavedLog }: LogEnvironmentViewProps) {
  const app = GetApp();
  const [ship, log] = [GetShip(), GetLog()];
  const { logs, auditLogEdits, recordAuditLogEdits, updateLog } = GetDay();
  const ft = app.getT("LogEnvironmentView");
  const [edited, onEdited, resetEdited] = useEditState();
  const [weather, setWeather] = useState<ILogWeather | undefined>(log.weather);
  const [saveError, setSaveError] = useState<unknown>(initSaveError);

  const editableFields = [
    "currentDirection",
    { name: "currentSpeed", type: "float" },
    { name: "humidity", type: "float" },
    { name: "pressure", type: "float" },
    "seaState",
    { name: "temperature", type: "float" },
    { name: "windDirection", type: "int" },
    { name: "windSpeed", type: "float" },
    { name: "weather", type: "int" },
  ];
  const formEditable = app.editable && editable;
  const selectWeatherOptions = useMemo(() => Array.from(Array(16).keys()).map((i) => {
    const n = i + 1;
    return {
      icon: "weather-" + n,
      title: ft("weather_" + n),
      value: n,
    };
  }), []);

  // Mirrors the server's `logs.update` field-editability rule (issue #529) — see
  // `Views/Log/editability.ts`. Replaces the old purely-live-NMEA-status check, which
  // didn't account for the ship's `editPermissiveness` level, the editable-age/recency
  // window, or a field already having been manually superseded on this log.
  const level = Ship.shipSettings(ship).editPermissiveness ?? IEditPermissivenessLevel.strict;
  const fieldEditable = useCallback((field: ILogEditableField) => {
    return formEditable && logFieldEditable(field, log, logs, auditLogEdits, level, Time.timeUtc(), app.forceLogEditable);
  }, [formEditable, log, logs, auditLogEdits, level, app.forceLogEditable]);

  // Issue #1142 (broader fix): mirrors every field's own `readOnly` condition below,
  // so `EditGroup` never even attempts to submit a value for a currently
  // non-editable field -- not even its own (possibly stale, display-rounded)
  // `defaultValue`. This is what stops an unrelated edit from ever resubmitting a
  // protected NMEA-capable field's rounded display value and manufacturing a
  // spurious "changed" edit server-side.
  const isFieldEditable = useCallback((field: string) => {
    switch (field) {
      case ILogEditableField.windDirection:
      case ILogEditableField.windSpeed:
      case ILogEditableField.pressure:
      case ILogEditableField.temperature:
      case ILogEditableField.humidity:
        return fieldEditable(field as ILogEditableField);
      case "currentDirection":
      case "seaState":
        // These two `PredefinedSelect`s are gated by the raw `editable` prop, not
        // `formEditable` -- mirrored here to match their own `readOnly` conditions
        // below exactly.
        return editable;
      default:
        // currentSpeed, weather: `.always`-editable, gated only by `formEditable`
        // (see their own `readOnly` conditions below).
        return formEditable;
    }
  }, [fieldEditable, formEditable, editable]);

  // Field-name -> localized-label map for `saveErrorMessage`: reuses each view's own
  // existing translation keys, including the combined labels these NMEA-capable
  // fields already share on-screen (e.g. `windDirection`/`windSpeed` under
  // `wind_direction_speed`). `currentDirection`/`currentSpeed`/`seaState`/`weather`
  // are `.always` (see `Log/editability.ts`) and can never actually appear in an
  // `EditNotAllowedError`, so they need no entry here.
  const saveErrorFieldLabels = useMemo(() => ({
    windDirection: ft("wind_direction_speed"),
    windSpeed: ft("wind_direction_speed"),
    pressure: ft("pressure"),
    temperature: ft("weather_temp_rel_humidity"),
    humidity: ft("weather_temp_rel_humidity"),
  }), [ft]);
  const saveErrorText = useMemo(() => saveErrorMessage(saveError, saveErrorFieldLabels, {
    fields: (labels) => ft("save_error_fields", { fields: labels }),
    generic: ft("save_error_generic"),
  }), [saveError, saveErrorFieldLabels, ft]);

  // Wired as EditGroup's `onSaveError` prop -- called with the rejected error on a
  // failed save, and with `undefined` at the start of every new submit attempt (see
  // EditGroup.tsx). A real error also resets `edited`, which is what stops
  // EditGroup's own auto-resubmit effect from silently resubmitting the now-reverted
  // (unchanged) form and clearing this error the instant that resubmit starts (issue
  // #1142). Mirrors useEditForm.ts's handleSaveError.
  const handleSaveError = useCallback((error: unknown) => {
    setSaveError(error);
    if (error !== undefined) {
      resetEdited();
    }
  }, [resetEdited]);

  // Current Direction

  const [currentDirection, setCurrentDirection] = useState<string | undefined>(log.currentDirection);
  const selectCurrentDirection = useCallback((value?: string) => {
    setCurrentDirection(value);
    if (onEdited) {
      onEdited();
    }
  }, [onEdited]);

  // SeaState

  const [seaState, setSeaState] = useState<string | undefined>(log.seaState);

  const selectSeaState = useCallback((value?: string) => {
    setSeaState(value);
    if (onEdited) {
      onEdited();
    }
  }, [onEdited]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const saveEdits = useCallback(async (_data: FormData, values: any) => {
    const result = await updateLog(log.id, values);
    if (result) {
      if (result.log) {
        recordSavedLog?.(result.log);
      }
      if (result.auditLogEdits) {
        recordAuditLogEdits(result.auditLogEdits);
      }
      resetEdited();
    }
  }, [log, recordAuditLogEdits, recordSavedLog, updateLog, resetEdited]);

  const selectWeather = useCallback((weather: number) => {
    setWeather(weather);
    onEdited();
  }, [weather]);

  return (
    <Block className="LogView-environment" onClick={onClick}>
      <EditGroup buttonFirst={true} changed={edited} editable={formEditable} editableFields={editableFields} error={saveErrorText} isFieldEditable={isFieldEditable} onSave={saveEdits} onSaveError={handleSaveError}>
        <BlockRow>
          <BlockField label={ft("wind_direction_speed")} className="block_field_small_16">
            <BlockFieldInputs>
              <input defaultValue={app.format("angle", log.windDirection)} name="windDirection" onChange={onEdited} readOnly={!fieldEditable(ILogEditableField.windDirection)} className="text-centered" />
              <BlockFieldInputsDivider />
              <input defaultValue={app.format("speed", log.windSpeed)} name="windSpeed" onChange={onEdited} readOnly={!fieldEditable(ILogEditableField.windSpeed)} className="text-centered" />
            </BlockFieldInputs>
          </BlockField>

          <BlockField label={ft("pressure")} className="block_field_small_10">
            <input defaultValue={app.format("pressure", log.pressure)} name="pressure" onChange={onEdited} readOnly={!fieldEditable(ILogEditableField.pressure)} className="text-centered" />
          </BlockField>
          <BlockField label={ft("weather_temp_rel_humidity")} className="block_field_small_16">
            <BlockFieldInputs>
              {!formEditable ? <span style={{ minWidth: "2em" }}><Icon icon={"weather-" + weather} /></span> : <Select className="weather_select" button={weather ? <Icon icon={"weather-" + weather} /> : "∅"} value={weather} name="weather" onSelect={selectWeather} options={selectWeatherOptions} readOnly={!formEditable} title={ft("weather_" + weather)} />}
              <BlockFieldInputsDivider />
              <input defaultValue={app.format("temperature", log.temperature)} name="temperature" onChange={onEdited} readOnly={!fieldEditable(ILogEditableField.temperature)} className="block_field_small_6 text-centered" />
              <BlockFieldInputsDivider />
              <input defaultValue={app.format("percentage", log.humidity)} name="humidity" onChange={onEdited} readOnly={!fieldEditable(ILogEditableField.humidity)} className="block_field_small_6 text-centered" />
            </BlockFieldInputs>
          </BlockField>
        </BlockRow>

        <BlockRow>
          <BlockField label={ft("current_direction_speed")} className="block_field_small_16">
            <BlockFieldInputs>
              <PredefinedSelect className="currentDirection_select text-centered" value={currentDirection} predefined={LogEnvironmentViewData.predefinedDirections} label={(v) => ft(`current_direction_${v}`)} customValue={ft("other_direction")} name={`currentDirection`} onSelect={selectCurrentDirection} readOnly={!editable} sort />
              <BlockFieldInputsDivider />
              <input defaultValue={app.format("speed", log.currentSpeed)} name="currentSpeed" onChange={onEdited} readOnly={!formEditable} className="text-centered fix-field-height" />
            </BlockFieldInputs>
          </BlockField>
          <BlockField label={ft("seastate")} className="block_field_small_30">
            <PredefinedSelect className="seastate_select" value={seaState} predefined={LogEnvironmentViewData.predefinedSeaStates} label={(v) => ft(`seastate_${v}`)} customValue={ft("other")} name={`seaState`} onSelect={selectSeaState} readOnly={!editable} />
          </BlockField>

        </BlockRow>
        <BlockRow>

        </BlockRow>
      </EditGroup>
    </Block>
  );
}
